import * as React from 'react';
import { useEffect, useState } from 'react';
import EarningsRateService, { EffectiveRate, UpsertRatePayload } from '../services/EarningsRateService';
import { Employee } from '../../../hooks/useEmployeeData';

interface AdminRatesScreenProps {
  employees?: Employee[];
}

export default function AdminRatesScreen({ employees = [] }: AdminRatesScreenProps): React.JSX.Element {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [rates, setRates] = useState<EffectiveRate[]>([]);
  const [dateStr, setDateStr] = useState('');
  const [hourly, setHourly] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!selectedEmployeeId) { setRates([]); return; }
      setLoading(true);
      try {
        const list = await EarningsRateService.listRates({ employee: selectedEmployeeId });
        const sorted = [...list].sort((a,b) => a.valid_from.localeCompare(b.valid_from));
        setRates(sorted);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedEmployeeId]);

  const handleAdd = async () => {
    if (!selectedEmployeeId || !dateStr || !hourly) return;
    const payload: UpsertRatePayload = {
      employee: selectedEmployeeId,
      valid_from: dateStr,
      hourly_rate: Number(hourly)
    };
    await EarningsRateService.upsertRate(payload);
    setDateStr(''); setHourly('');
    const list = await EarningsRateService.listRates({ employee: selectedEmployeeId });
    setRates([...list].sort((a,b) => a.valid_from.localeCompare(b.valid_from)));
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Diesen Stundensatz wirklich löschen?')) return;
    await EarningsRateService.deleteRate(id);
    setRates(prev => prev.filter(r => r.id !== id));
  };

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ margin: 0, marginBottom: 12 }}>Hourly Rates (Admin)</h2>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <select value={selectedEmployeeId ?? ''} onChange={e => setSelectedEmployeeId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">(Mitarbeiter wählen)</option>
          {employees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
          ))}
        </select>
        {selectedEmployeeId && <span>Employee ID: {selectedEmployeeId}</span>}
      </div>

      {selectedEmployeeId && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)} />
          <input type="number" step="0.01" placeholder="€/h" value={hourly} onChange={e => setHourly(e.target.value)} />
          <button onClick={handleAdd}>Hinterlegen</button>
        </div>
      )}

      {loading && <div>Lade...</div>}
      {!loading && selectedEmployeeId && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 6 }}>Gültig ab</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid #ddd', padding: 6 }}>€/h</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 6 }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {rates.map(r => (
              <tr key={r.id}>
                <td style={{ padding: 6 }}>{r.valid_from}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>{r.hourly_rate.toFixed(2)}</td>
                <td style={{ padding: 6 }}>
                  <button onClick={() => handleDelete(r.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {rates.length === 0 && (
              <tr>
                <td colSpan={3} style={{ padding: 6, color: '#666' }}>(Noch keine Raten)</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}


